import React, { useState, useEffect, useMemo } from 'react';
import { Button, Card, Switch, Alert, Spinner, Badge, Modal } from '@aidkitorg/component-library';
import { get_deployment, usePost } from '../API';

interface FeatureFlag {
  key: string;
  defaultValue: boolean;
  description: string;
}

interface FeatureFlagStatus {
  key: string;
  enabled: boolean;
  found: boolean;
  source: string;
  value: boolean;
  updatedAt?: number;
  updatedBy?: string;
  updatedByName?: string;
}

export const FeatureFlagPage: React.FC = () => {
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [flagStatuses, setFlagStatuses] = useState<Map<string, FeatureFlagStatus>>(new Map());
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmationModal, setConfirmationModal] = useState<{
    open: boolean;
    flagKey: string;
    currentValue: boolean;
    newValue: boolean;
    description: string;
  } | null>(null);

  // API hooks
  const getFeatureFlags = usePost('/admin/feature-flags');
  const getFeatureFlag = usePost('/admin/feature-flag');
  const setFeatureFlag = usePost('/admin/feature-flag/set');

  // Load all available flags and their current status
  const loadFlags = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get all available flags
      const availableFlags = await getFeatureFlags({});

      if (!availableFlags || !Array.isArray(availableFlags)) {
        throw new Error('Failed to load system feature flags');
      }

      setFlags(availableFlags);

      // Get current status for each flag
      const statusMap = new Map<string, FeatureFlagStatus>();
      const statusPromises = availableFlags.map(async (flag: FeatureFlag) => {
        try {
          const status = await getFeatureFlag({ key: flag.key });
          if (status) {
            statusMap.set(flag.key, status);
          }
        } catch (err) {
          console.warn(`Failed to get status for flag ${flag.key}:`, err);
          // Set default status if we can't get the current value
          statusMap.set(flag.key, {
            key: flag.key,
            enabled: flag.defaultValue,
            found: false,
            source: 'default',
            value: flag.defaultValue
          });
        }
      });

      await Promise.all(statusPromises);
      setFlagStatuses(statusMap);

    } catch (err) {
      console.error('Failed to load feature flags:', err);
      setError(err instanceof Error ? err.message : 'Failed to load feature flags');
    } finally {
      setLoading(false);
    }
  };

  // Show confirmation modal for flag toggle
  const toggleFlag = (flagKey: string, currentEnabled: boolean) => {
    const flag = flags.find(f => f.key === flagKey);
    if (!flag) return;

    const newEnabled = !currentEnabled;
    setConfirmationModal({
      open: true,
      flagKey,
      currentValue: currentEnabled,
      newValue: newEnabled,
      description: flag.description
    });
  };

  // Actually perform the flag change after confirmation
  const confirmFlagChange = async () => {
    if (!confirmationModal) return;

    const { flagKey, newValue } = confirmationModal;

    try {
      setUpdating(prev => new Set(prev).add(flagKey));
      setError(null);
      setSuccess(null);
      setConfirmationModal(null); // Close modal

      await setFeatureFlag({
        key: flagKey,
        enabled: newValue
      });

      // Update local state
      loadFlags();

      setSuccess(`Successfully set ${flagKey} to ${newValue ? 'true' : 'false'}`);

    } catch (err) {
      console.error(`Failed to toggle flag ${flagKey}:`, err);
      setError(err instanceof Error ? err.message : `Failed to update ${flagKey}`);
    } finally {
      setUpdating(prev => {
        const newSet = new Set(prev);
        newSet.delete(flagKey);
        return newSet;
      });
    }
  };

  // Cancel the flag change
  const cancelFlagChange = () => {
    setConfirmationModal(null);
  };

  // Get status badge for a flag
  const getStatusBadge = (status: FeatureFlagStatus, defaultValue: boolean) => {
    if (!status.found) {
      return (
        <Badge variant={defaultValue ? "success" : "error"}>
          {defaultValue ? "True (default)" : "False (default)"}
        </Badge>
      );
    }

    if (status.enabled) {
      return <Badge variant="success">True</Badge>;
    } else {
      return <Badge variant="error">False</Badge>;
    }
  };

  // Get source description
  const getSourceDescription = (source: string) => {
    switch (source) {
      case 'database':
        return 'Set in database';
      case 'default':
        return 'Using default value';
      case 'fallback':
        return 'Using fallback value';
      default:
        return source;
    }
  };
  const deploymentName = useMemo(() => get_deployment(), []);

  useEffect(() => {
    loadFlags();
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <Spinner size="large" />
        <span className="ml-3">Loading system feature flags...</span>
      </div>
    );
  }
  return (
    <div>
      <div className={'d-flex h-14 px-[15px] justify-content-between align-items-center mb-4 ' + (process.env.NODE_ENV === 'development' ? 'bg-green-200' : 'bg-blue-200')}>
        <div>
          <h3>{`[${deploymentName}] System Feature Flags`}</h3>
        </div>
        <Button onClick={loadFlags} variant="secondary">
          Refresh
        </Button>
      </div>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">

            {error && (
              <Alert variant="danger" className="mb-4">
                {error}
              </Alert>
            )}

            {success && (
              <Alert variant="success" className="mb-4">
                {success}
              </Alert>
            )}

            {flags.length === 0 ? (
              <Card className="p-4 text-center">
                <p className="text-muted">No system feature flags available</p>
              </Card>
            ) : (
              <div className="row">
                {flags.map((flag) => {
                  const status = flagStatuses.get(flag.key);
                  const isUpdating = updating.has(flag.key);

                  return (
                    <div key={flag.key} className="col-12 col-md-6 col-lg-4 mb-4">
                      <Card className="h-100">
                        <div className="p-3">
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <div className="flex-grow-1">
                              <h5 className="mb-1">{flag.key}</h5>
                              <p className="text-muted small mb-2">{flag.description}</p>
                            </div>
                            <div className="ml-2">
                              {status && getStatusBadge(status, flag.defaultValue)}
                            </div>
                          </div>

                          <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="small text-muted">Status:</span>
                              <Switch
                                label={`Toggle ${flag.key} feature flag`}
                                hideLabel={true}
                                checked={status?.enabled ?? flag.defaultValue}
                                onCheckedChange={() => toggleFlag(flag.key, status?.enabled ?? flag.defaultValue)}
                                disabled={isUpdating}
                              />
                            </div>
                            {status && (
                              <div className="mt-2">
                                <small className="text-muted">
                                  {getSourceDescription(status.source)}
                                </small>
                                {status.updatedAt && (
                                  <div className="mt-1">
                                    <small className="text-muted">
                                      Last updated: {new Date(status.updatedAt).toLocaleString()}
                                    </small>
                                  </div>
                                )}
                                {status.updatedByName && (
                                  <div className="mt-1">
                                    <small className="text-muted">
                                      Updated by: {status.updatedByName}
                                    </small>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="border-top pt-3">
                            <div className="d-flex justify-content-between">
                              <span className="small text-muted">Default:</span>
                              <span className="small">
                                {flag.defaultValue ? 'True' : 'False'}
                              </span>
                            </div>
                          </div>

                          {isUpdating && (
                            <div className="d-flex align-items-center mt-2">
                              <Spinner size="small" />
                              <span className="ml-2 small text-muted">Updating...</span>
                            </div>
                          )}
                        </div>
                      </Card>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mt-5">
              <Card className="p-4">
                <h5>About System Feature Flags</h5>
                <p className="text-muted mb-3">
                  System feature flags control core platform functionality. They are similar to experimental flags in the Distro ProgramConfig,
                  but are stored in the postgres database instead of the distro file.
                  Changes to these flags may affect system behavior and should be made with EXTREME caution.
                </p>
                <div className="row">
                  <div className="col-md-6">
                    <h6>Status Indicators:</h6>
                    <ul className="small">
                      <li><Badge variant="success">True</Badge> - Flag is set to true in the database</li>
                      <li><Badge variant="error">False</Badge> - Flag is set to false in the database</li>
                    </ul>
                  </div>
                  <div className="col-md-6">
                    <h6>Best Practices:</h6>
                    <ul className="small">
                      <li>Test flag changes in a development environment first</li>
                      <li>Monitor system behavior after enabling new features</li>
                      <li>Document any flag changes for your team</li>
                    </ul>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>

        {/* Confirmation Modal */}
        {confirmationModal && (
          <Modal
            open={confirmationModal.open}
            onClose={cancelFlagChange}
            title="Confirm Feature Flag Change"
            size="md"
            preventBackdropClick={false}
          >
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      EXTREME CAUTION REQUIRED
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>System feature flags control critical platform functionality.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <p className="text-2xl text-gray-600 font-mono bg-gray-50 px-2 py-1 rounded">
                    {confirmationModal.flagKey}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600 pb-10">{confirmationModal.description}</p>
                </div>

                <div className="flex items-center justify-center space-x-4">
                  <div className="text-center">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Current Value</h4>
                    <Badge className="text-2xl" variant={confirmationModal.currentValue ? "success" : "error"}>
                      {confirmationModal.currentValue ? "True" : "False"}
                    </Badge>
                  </div>

                  <div className="flex items-center">
                    <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>

                  <div className="text-center">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">New Value</h4>
                    <Badge className="text-2xl" variant={confirmationModal.newValue ? "success" : "error"}>
                      {confirmationModal.newValue ? "True" : "False"}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button
                  variant="secondary"
                  onClick={cancelFlagChange}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={confirmFlagChange}
                >
                  Yes, Change Flag
                </Button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};
